import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';
import { 
  Shield, Users, Image, MessageCircle, Check, X, Search, Trash2, Eye, 
  Plus, Edit2, Mail, Lock, Crown, UserCheck, MapPin, Calendar, Heart,
  ArrowLeft, Send, Camera, AlertCircle, BarChart3, AlertTriangle, FileWarning
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export default function AdminPage() {
  const { user, api } = useAuth();
  const [users, setUsers] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totals, setTotals] = useState({ users: 0, messages: 0 });
  
  // Dashboard stats
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Reports state
  const [reports, setReports] = useState([]);
  const [reportsStats, setReportsStats] = useState(null);
  const [reportsFilter, setReportsFilter] = useState('pending');
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Photo viewer state
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Validation modal states
  const [validationRejectionModalOpen, setValidationRejectionModalOpen] = useState(false);
  const [selectedValidationProfile, setSelectedValidationProfile] = useState(null);
  const [validationRejectionReason, setValidationRejectionReason] = useState('');

  // Create user form
  const [newUser, setNewUser] = useState({
    email: '',
    pseudo: '',
    password: '',
    is_certified: false,
    is_subscribed: false,
    is_verified_by_team: false,
    is_admin: false
  });

  // Edit user form
  const [editUser, setEditUser] = useState({
    is_certified: false,
    is_subscribed: false,
    is_verified_by_team: false,
    is_admin: false,
    is_validated: false,
    is_ambassador: false,
    subscription_plan: '',
    new_password: ''
  });

  useEffect(() => {
    if (user.is_admin) {
      fetchData();
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, photosRes, messagesRes, validationsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/photos'),
        api.get('/admin/messages'),
        api.get('/admin/validations/pending')
      ]);
      setUsers(usersRes.data.users || usersRes.data);
      setPhotos(photosRes.data);
      setMessages(messagesRes.data.messages || messagesRes.data);
      setVerifications(validationsRes.data || []);
      setTotals({
        users: usersRes.data.total || (usersRes.data.users || usersRes.data).length,
        messages: messagesRes.data.total || (messagesRes.data.messages || messagesRes.data).length
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Fetch reports
  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const response = await api.get(`/admin/reports?status=${reportsFilter}`);
      setReports(response.data.reports || []);
      setReportsStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Erreur lors du chargement des signalements');
    } finally {
      setLoadingReports(false);
    }
  };

  // Update report status
  const updateReportStatus = async (reportId, newStatus) => {
    try {
      await api.put(`/admin/reports/${reportId}`, {
        status: newStatus,
        admin_notes: adminNotes
      });
      toast.success('Signalement mis à jour');
      setReportModalOpen(false);
      setSelectedReport(null);
      setAdminNotes('');
      fetchReports();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Delete report
  const deleteReport = async (reportId) => {
    if (!window.confirm('Supprimer ce signalement ?')) return;
    try {
      await api.delete(`/admin/reports/${reportId}`);
      toast.success('Signalement supprimé');
      fetchReports();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Fetch reports when filter changes
  useEffect(() => {
    fetchReports();
  }, [reportsFilter]);

  const createUser = async () => {
    try {
      await api.post('/admin/users', newUser);
      toast.success('Utilisateur créé');
      setCreateModalOpen(false);
      setNewUser({
        email: '',
        pseudo: '',
        password: '',
        is_certified: false,
        is_subscribed: false,
        is_verified_by_team: false,
        is_admin: false
      });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(message);
    }
  };

  const updateUser = async () => {
    if (!selectedUser) return;
    try {
      // Only send non-empty values
      const dataToSend = {};
      if (editUser.is_certified !== undefined) dataToSend.is_certified = editUser.is_certified;
      if (editUser.is_subscribed !== undefined) dataToSend.is_subscribed = editUser.is_subscribed;
      if (editUser.is_verified_by_team !== undefined) dataToSend.is_verified_by_team = editUser.is_verified_by_team;
      if (editUser.is_admin !== undefined) dataToSend.is_admin = editUser.is_admin;
      if (editUser.is_validated !== undefined) dataToSend.is_validated = editUser.is_validated;
      if (editUser.is_ambassador !== undefined) dataToSend.is_ambassador = editUser.is_ambassador;
      if (editUser.subscription_plan) dataToSend.subscription_plan = editUser.subscription_plan;
      if (editUser.new_password && editUser.new_password.trim()) {
        dataToSend.new_password = editUser.new_password;
      }
      
      await api.put(`/admin/users/${selectedUser.id}`, dataToSend);
      toast.success(editUser.new_password ? 'Utilisateur mis à jour et mot de passe réinitialisé' : 'Utilisateur mis à jour');
      setEditModalOpen(false);
      setEditUser({ ...editUser, new_password: '' }); // Clear password field
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditModal = (u) => {
    setSelectedUser(u);
    setEditUser({
      is_certified: u.is_certified || false,
      is_subscribed: u.is_subscribed || false,
      is_verified_by_team: u.is_verified_by_team || false,
      is_admin: u.is_admin || false,
      is_validated: u.is_validated || false,
      is_ambassador: u.is_ambassador || false,
      subscription_plan: u.subscription_plan || '',
      new_password: ''
    });
    setEditModalOpen(true);
  };

  const openDetailsModal = async (u) => {
    setSelectedUser(u);
    setDetailsModalOpen(true);
    setLoadingDetails(true);
    
    try {
      const response = await api.get(`/admin/users/${u.id}/details`);
      setUserDetails(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setLoadingDetails(false);
    }
  };

  const deletePhoto = async (userId, photoId) => {
    try {
      await api.delete(`/admin/photos/${userId}/${photoId}`);
      toast.success('Photo supprimée');
      // Refresh details if open
      if (userDetails && userDetails.user.id === userId) {
        const response = await api.get(`/admin/users/${userId}/details`);
        setUserDetails(response.data);
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openVerificationModal = (verification) => {
    setSelectedVerification(verification);
    setRejectionReason('');
    setVerificationModalOpen(true);
  };

  const respondToVerification = async (approved) => {
    if (!selectedVerification) return;
    
    try {
      await api.post('/admin/verifications/respond', {
        user_id: selectedVerification.user_id,
        approved,
        rejection_reason: approved ? null : rejectionReason
      });
      toast.success(approved ? 'Vérification approuvée' : 'Vérification refusée');
      setVerificationModalOpen(false);
      setSelectedVerification(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la réponse');
    }
  };

  // Admin validation functions
  const handleAdminValidation = async (userId, approved) => {
    try {
      await api.post('/admin/validations/vote', {
        profile_id: userId,
        approved,
        rejection_reason: approved ? null : validationRejectionReason
      });
      toast.success(approved ? 'Profil validé avec succès' : 'Profil rejeté - utilisateur notifié');
      setValidationRejectionModalOpen(false);
      setSelectedValidationProfile(null);
      setValidationRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const openRejectionModal = (item) => {
    setSelectedValidationProfile(item);
    setValidationRejectionReason('');
    setValidationRejectionModalOpen(true);
  };

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="admin-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="glass-card p-12">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-white mb-4">
              Accès refusé
            </h1>
            <p className="text-gray-400">
              Cette page est réservée aux administrateurs.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const filteredUsers = users.filter(u => 
    u.pseudo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Shield className="w-8 h-8 text-gold" />
            <div>
              <h1 className="font-serif text-3xl gold-gradient-text">Administration</h1>
              <p className="text-gray-400 text-sm">Gestion des membres et du contenu</p>
            </div>
          </div>
          
          {/* Create User Button */}
          <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
            <DialogTrigger asChild>
              <Button className="btn-gold flex items-center gap-2" data-testid="create-user-btn">
                <Plus className="w-4 h-4" /> Créer un membre
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-dark-paper border-white/10 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl gold-gradient-text">Créer un membre</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Email</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="bg-dark-subtle border-white/20 text-white"
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Pseudo</Label>
                  <Input
                    type="text"
                    value={newUser.pseudo}
                    onChange={(e) => setNewUser({ ...newUser, pseudo: e.target.value })}
                    className="bg-dark-subtle border-white/20 text-white"
                    placeholder="Pseudo unique"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Mot de passe</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="bg-dark-subtle border-white/20 text-white"
                    placeholder="••••••••"
                  />
                </div>
                
                <div className="space-y-3 pt-4 border-t border-white/10">
                  <Label className="text-gray-400">Statuts</Label>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Certifié</span>
                    <Switch
                      checked={newUser.is_certified}
                      onCheckedChange={(checked) => setNewUser({ ...newUser, is_certified: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Abonné Ambassadeur</span>
                    <Switch
                      checked={newUser.is_subscribed}
                      onCheckedChange={(checked) => setNewUser({ ...newUser, is_subscribed: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Vérifié par l'équipe</span>
                    <Switch
                      checked={newUser.is_verified_by_team}
                      onCheckedChange={(checked) => setNewUser({ ...newUser, is_verified_by_team: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Administrateur</span>
                    <Switch
                      checked={newUser.is_admin}
                      onCheckedChange={(checked) => setNewUser({ ...newUser, is_admin: checked })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setCreateModalOpen(false)} className="btn-outline-gold">
                  Annuler
                </Button>
                <Button onClick={createUser} className="btn-gold">
                  Créer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={<Users className="w-6 h-6" />} label="Membres" value={totals.users} />
          <StatCard 
            icon={<Shield className="w-6 h-6" />} 
            label="Certifiés" 
            value={users.filter(u => u.is_certified).length} 
          />
          <StatCard icon={<Image className="w-6 h-6" />} label="Photos" value={photos.length} />
          <StatCard icon={<MessageCircle className="w-6 h-6" />} label="Messages" value={totals.messages} />
          <StatCard 
            icon={<Camera className="w-6 h-6" />} 
            label="Validations" 
            value={verifications.length}
            highlight={verifications.length > 0}
          />
        </div>

        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="bg-dark-paper border border-white/10 w-full h-auto flex-wrap gap-1 p-1">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
              <span className="hidden sm:inline">Tableau de bord</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Membres
            </TabsTrigger>
            <TabsTrigger value="validations" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold relative text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <Camera className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Valid.
              {verifications.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-gold text-dark text-xs rounded-full">{verifications.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="photos" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <Image className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Photos
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
              <span className="hidden sm:inline">Messages</span>
              <span className="sm:hidden">Msg</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold relative text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
              <span className="hidden sm:inline">Signalements</span>
              <span className="sm:hidden">Sign.</span>
              {reportsStats?.pending > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {reportsStats.pending}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-6">
            <div className="space-y-6">
              <h2 className="font-serif text-2xl text-gold">Récapitulatif des profils</h2>
              
              {loadingStats ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                </div>
              ) : stats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Pending Validation */}
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-yellow-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">En attente de validation</h3>
                        <p className="text-3xl font-bold text-yellow-500">{stats.pending_validation.total}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.pending_validation.homme}</p>
                        <p className="text-xs text-gray-500">Hommes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.pending_validation.femme}</p>
                        <p className="text-xs text-gray-500">Femmes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.pending_validation.couple}</p>
                        <p className="text-xs text-gray-500">Couples</p>
                      </div>
                    </div>
                  </div>

                  {/* Validated Never Subscribed */}
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <UserCheck className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">Validés, jamais abonnés</h3>
                        <p className="text-3xl font-bold text-blue-500">{stats.validated_never_subscribed.total}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.validated_never_subscribed.homme}</p>
                        <p className="text-xs text-gray-500">Hommes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.validated_never_subscribed.femme}</p>
                        <p className="text-xs text-gray-500">Femmes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.validated_never_subscribed.couple}</p>
                        <p className="text-xs text-gray-500">Couples</p>
                      </div>
                    </div>
                  </div>

                  {/* Active Subscription */}
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Crown className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">Abonnement en cours</h3>
                        <p className="text-3xl font-bold text-green-500">{stats.active_subscription.total}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.active_subscription.homme}</p>
                        <p className="text-xs text-gray-500">Hommes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.active_subscription.femme}</p>
                        <p className="text-xs text-gray-500">Femmes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.active_subscription.couple}</p>
                        <p className="text-xs text-gray-500">Couples</p>
                      </div>
                    </div>
                  </div>

                  {/* Expired Subscription */}
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                        <X className="w-5 h-5 text-red-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">Abonnement expiré</h3>
                        <p className="text-3xl font-bold text-red-500">{stats.expired_subscription.total}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.expired_subscription.homme}</p>
                        <p className="text-xs text-gray-500">Hommes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.expired_subscription.femme}</p>
                        <p className="text-xs text-gray-500">Femmes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{stats.expired_subscription.couple}</p>
                        <p className="text-xs text-gray-500">Couples</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">Erreur lors du chargement des statistiques</p>
              )}
              
              {/* Refresh button */}
              <Button 
                onClick={fetchStats} 
                className="btn-outline-gold"
                disabled={loadingStats}
              >
                Actualiser les statistiques
              </Button>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <div className="glass-card p-4 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Rechercher un membre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-dark-subtle border-white/20 pl-10 text-white"
                  data-testid="search-users"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
              </div>
            ) : (
              <div className="glass-card overflow-hidden">
                <ScrollArea className="h-[500px]">
                  <table className="w-full" data-testid="users-table">
                    <thead className="bg-dark-subtle sticky top-0">
                      <tr>
                        <th className="text-left p-4 text-gray-400 text-sm">Membre</th>
                        <th className="text-left p-4 text-gray-400 text-sm">Email</th>
                        <th className="text-left p-4 text-gray-400 text-sm">Statut</th>
                        <th className="text-left p-4 text-gray-400 text-sm">Inscription</th>
                        <th className="text-right p-4 text-gray-400 text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                          <td className="p-4">
                            <button 
                              onClick={() => openDetailsModal(u)}
                              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                            >
                              <Avatar className="w-10 h-10 border border-white/10">
                                {u.profile?.profile_picture ? (
                                  <AvatarImage src={getImageUrl(u.profile.profile_picture)} />
                                ) : (
                                  <AvatarFallback className="bg-dark-subtle text-gold">
                                    {u.pseudo?.[0]?.toUpperCase()}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              <div className="text-left">
                                <p className="text-white font-medium hover:text-gold transition-colors">{u.pseudo}</p>
                                <p className="text-gray-500 text-xs capitalize">{u.profile?.profile_type || 'Profil incomplet'}</p>
                              </div>
                            </button>
                          </td>
                          <td className="p-4 text-gray-400 text-sm">{u.email}</td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-1">
                              {u.is_validated ? (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs">Profil validé</span>
                              ) : (
                                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs">Non validé</span>
                              )}
                              {u.is_subscribed && (
                                <span className="px-2 py-0.5 bg-gold text-dark text-xs font-semibold">Abonné</span>
                              )}
                              {u.is_ambassador && (
                                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs">Ambassadeur</span>
                              )}
                              {u.is_admin && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs">Admin</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-gray-500 text-sm">
                            {new Date(u.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="p-4">
                            <div className="flex justify-end gap-2">
                              <Button
                                onClick={() => openDetailsModal(u)}
                                className="px-3 py-1 bg-gold/20 text-gold text-xs hover:bg-gold/30"
                                data-testid={`view-${u.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" /> Voir
                              </Button>
                              <Button
                                onClick={() => openEditModal(u)}
                                className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs hover:bg-blue-500/30"
                                data-testid={`edit-${u.id}`}
                              >
                                <Edit2 className="w-3 h-3 mr-1" /> Modifier
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    className="px-3 py-1 bg-red-500/20 text-red-400 text-xs hover:bg-red-500/30"
                                    data-testid={`delete-${u.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-dark-paper border-white/10">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-white">Supprimer ce membre ?</AlertDialogTitle>
                                    <AlertDialogDescription className="text-gray-400">
                                      Cette action est irréversible. Toutes les données de {u.pseudo} seront supprimées.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="bg-dark-subtle text-white border-white/10">
                                      Annuler
                                    </AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => deleteUser(u.id)}
                                      className="bg-red-600 text-white hover:bg-red-700"
                                    >
                                      Supprimer
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            )}
          </TabsContent>

          {/* Verifications Tab */}
          <TabsContent value="validations" className="mt-6">
            {verifications.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">Aucun profil en attente de validation</h3>
                <p className="text-gray-400">Tous les profils ont été examinés.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="validations-grid">
                {verifications.map((item) => (
                  <div key={item.user.id} className={`glass-card p-6 ${!item.profile_complete ? 'border border-yellow-500/30' : ''}`}>
                    {/* Incomplete profile badge */}
                    {!item.profile_complete && (
                      <div className="mb-3 px-3 py-1 bg-yellow-500/20 rounded text-yellow-400 text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Profil incomplet {!item.has_profile ? '(pas de profil créé)' : !item.has_photos ? '(pas de photos)' : ''}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 mb-4">
                      <Avatar className="w-14 h-14 border-2 border-gold/30">
                        {item.profile?.profile_picture ? (
                          <AvatarImage src={getImageUrl(item.profile.profile_picture)} />
                        ) : (
                          <AvatarFallback className="bg-dark-subtle text-gold text-xl">
                            {item.user?.pseudo?.[0]?.toUpperCase() || '?'}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="text-white font-semibold">{item.user?.pseudo || item.profile?.pseudo}</h3>
                        <p className="text-gray-500 text-sm">
                          {item.profile?.age ? `${item.profile.age} ans` : 'Âge non renseigné'} • {item.profile?.location || 'Lieu non renseigné'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-green-400">{item.votes?.approvals || 0} ✓</span>
                          <span className="text-xs text-red-400">{item.votes?.rejections || 0} ✗</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Profile Photos Preview */}
                    {item.profile?.photos?.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {item.profile.photos.slice(0, 3).map((photo, index) => (
                          <div key={index} className="aspect-square overflow-hidden rounded bg-dark-subtle">
                            <img
                              src={getImageUrl(photo.url)}
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mb-4 p-4 bg-dark-subtle rounded text-center text-gray-500 text-sm">
                        Aucune photo
                      </div>
                    )}
                    
                    {/* Description preview */}
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2 whitespace-pre-wrap">
                      {item.profile?.description || 'Pas de description'}
                    </p>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleAdminValidation(item.user.id, true)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`validate-${item.user.id}`}
                        disabled={!item.profile_complete}
                        title={!item.profile_complete ? 'Profil incomplet - ne peut pas être validé' : ''}
                      >
                        <Check className="w-4 h-4 mr-1" /> Valider
                      </Button>
                      <Button 
                        onClick={() => openRejectionModal(item)}
                        className="flex-1 bg-red-600/20 hover:bg-red-600/30 text-red-400"
                        data-testid={`reject-${item.user.id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Rejeter
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="photos-grid">
              {photos.map((photo, index) => (
                <div key={photo.id || index} className="relative group">
                  <div 
                    className="aspect-square overflow-hidden rounded-sm cursor-pointer"
                    onClick={() => {
                      setSelectedPhoto(photo);
                      setPhotoViewerOpen(true);
                    }}
                    data-testid={`photo-thumbnail-${photo.id || index}`}
                  >
                    <img
                      src={getImageUrl(photo.url)}
                      alt={`Photo by ${photo.pseudo}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-end justify-between p-2 opacity-0 group-hover:opacity-100 pointer-events-none">
                    <span className="text-white text-xs bg-dark/80 px-2 py-1">
                      {photo.pseudo}
                      {photo.is_private && <span className="text-gold ml-1">(privée)</span>}
                    </span>
                  </div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      deletePhoto(photo.user_id, photo.id);
                    }}
                    className="absolute top-2 right-2 w-6 h-6 p-0 bg-red-500 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${photo.id || index}`}
                  >
                    <Trash2 className="w-3 h-3 text-white" />
                  </Button>
                </div>
              ))}
            </div>
            
            {/* Photo Viewer Modal */}
            <Dialog open={photoViewerOpen} onOpenChange={setPhotoViewerOpen}>
              <DialogContent className="bg-dark-paper border-white/10 max-w-4xl max-h-[90vh] p-0 overflow-hidden">
                {selectedPhoto && (
                  <div className="flex flex-col">
                    {/* Photo Header */}
                    <div className="flex items-center justify-between p-4 border-b border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold font-semibold">
                          {selectedPhoto.pseudo?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{selectedPhoto.pseudo}</h3>
                          <p className="text-gray-400 text-sm">
                            {selectedPhoto.is_private ? 'Photo privée' : 'Photo publique'}
                            {selectedPhoto.created_at && ` • ${new Date(selectedPhoto.created_at).toLocaleDateString('fr-FR')}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => {
                            deletePhoto(selectedPhoto.user_id, selectedPhoto.id);
                            setPhotoViewerOpen(false);
                          }}
                          className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                          data-testid="delete-photo-modal"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                    
                    {/* Photo Display */}
                    <div className="flex items-center justify-center bg-black p-4 min-h-[400px] max-h-[70vh]">
                      <img
                        src={getImageUrl(selectedPhoto.url)}
                        alt={`Photo by ${selectedPhoto.pseudo}`}
                        className="max-w-full max-h-[65vh] object-contain"
                        data-testid="photo-viewer-image"
                      />
                    </div>
                    
                    {/* Photo Info */}
                    <div className="p-4 border-t border-white/10 bg-dark-subtle">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">
                          ID: {selectedPhoto.id?.substring(0, 8)}...
                        </span>
                        <span className="text-gray-400">
                          Utilisateur: {selectedPhoto.user_id?.substring(0, 8)}...
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-6">
            <div className="glass-card overflow-hidden">
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-white/5" data-testid="messages-list">
                  {messages.map((msg) => (
                    <div key={msg.id} className="p-4 hover:bg-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gold text-sm">{msg.sender_pseudo}</span>
                        <span className="text-gray-500 text-xs">
                          {new Date(msg.created_at).toLocaleString('fr-FR')}
                        </span>
                      </div>
                      <p className="text-gray-300 text-sm">{msg.content}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        → Destinataire: {msg.recipient_pseudo || msg.recipient_id}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-6">
            <div className="space-y-6">
              {/* Reports Stats */}
              {reportsStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="glass-card p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-400">{reportsStats.pending}</div>
                    <div className="text-sm text-gray-400">En attente</div>
                  </div>
                  <div className="glass-card p-4 text-center">
                    <div className="text-2xl font-bold text-blue-400">{reportsStats.reviewed}</div>
                    <div className="text-sm text-gray-400">Examinés</div>
                  </div>
                  <div className="glass-card p-4 text-center">
                    <div className="text-2xl font-bold text-green-400">{reportsStats.action_taken}</div>
                    <div className="text-sm text-gray-400">Action prise</div>
                  </div>
                  <div className="glass-card p-4 text-center">
                    <div className="text-2xl font-bold text-gray-400">{reportsStats.dismissed}</div>
                    <div className="text-sm text-gray-400">Rejetés</div>
                  </div>
                </div>
              )}

              {/* Filter */}
              <div className="flex items-center gap-4">
                <Label className="text-gray-400">Filtrer par statut:</Label>
                <Select value={reportsFilter} onValueChange={setReportsFilter}>
                  <SelectTrigger className="w-48 bg-dark-subtle border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-dark-paper border-white/10">
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="reviewed">Examinés</SelectItem>
                    <SelectItem value="action_taken">Action prise</SelectItem>
                    <SelectItem value="dismissed">Rejetés</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reports List */}
              <div className="glass-card overflow-hidden">
                {loadingReports ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                  </div>
                ) : reports.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileWarning className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun signalement {reportsFilter !== 'all' ? 'avec ce statut' : ''}</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="divide-y divide-white/5">
                      {reports.map((report) => (
                        <div key={report.id} className="p-4 hover:bg-white/5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4 flex-1">
                              {/* Reported User Avatar */}
                              <div className="w-12 h-12 rounded-full overflow-hidden bg-dark-subtle flex-shrink-0">
                                {report.reported_profile?.profile_picture ? (
                                  <img
                                    src={getImageUrl(report.reported_profile.profile_picture)}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                                    <Users className="w-6 h-6" />
                                  </div>
                                )}
                              </div>

                              {/* Report Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-white">
                                    {report.reported_user_pseudo}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    report.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                    report.status === 'reviewed' ? 'bg-blue-500/20 text-blue-400' :
                                    report.status === 'action_taken' ? 'bg-green-500/20 text-green-400' :
                                    'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {report.status === 'pending' ? 'En attente' :
                                     report.status === 'reviewed' ? 'Examiné' :
                                     report.status === 'action_taken' ? 'Action prise' :
                                     'Rejeté'}
                                  </span>
                                </div>
                                
                                <div className="flex items-center gap-2 text-sm text-red-400 mb-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  <span className="font-medium">{report.reason_label}</span>
                                </div>
                                
                                {report.details && (
                                  <p className="text-sm text-gray-400 mb-2 line-clamp-2">
                                    "{report.details}"
                                  </p>
                                )}
                                
                                <div className="text-xs text-gray-500">
                                  Signalé par <span className="text-gold">{report.reporter_pseudo}</span> le {new Date(report.created_at).toLocaleDateString('fr-FR')}
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 hover:bg-white/5"
                                onClick={() => {
                                  setSelectedReport(report);
                                  setAdminNotes(report.admin_notes || '');
                                  setReportModalOpen(true);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                onClick={() => deleteReport(report.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit User Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl gold-gradient-text">
                Modifier {selectedUser?.pseudo}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <Label className="text-gray-400">Statuts du membre</Label>
                
                {/* Profil validé */}
                <div className="flex items-center justify-between p-3 bg-dark-subtle rounded">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-gray-300">Profil validé</span>
                  </div>
                  <Switch
                    checked={editUser.is_validated}
                    onCheckedChange={(checked) => setEditUser({ ...editUser, is_validated: checked })}
                  />
                </div>

                {/* Abonnement */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-dark-subtle rounded">
                    <Crown className="w-4 h-4 text-gold" />
                    <span className="text-sm text-gray-300">Abonnement</span>
                  </div>
                  <Select
                    value={editUser.is_subscribed ? (editUser.subscription_plan || 'monthly') : 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setEditUser({ ...editUser, is_subscribed: false, subscription_plan: null });
                      } else {
                        setEditUser({ ...editUser, is_subscribed: true, subscription_plan: value });
                      }
                    }}
                  >
                    <SelectTrigger className="bg-dark-subtle border-white/20 text-white">
                      <SelectValue placeholder="Sélectionner un abonnement" />
                    </SelectTrigger>
                    <SelectContent className="bg-dark-paper border-white/10">
                      <SelectItem value="none">Aucun abonnement</SelectItem>
                      <SelectItem value="weekly">1 semaine</SelectItem>
                      <SelectItem value="monthly">1 mois</SelectItem>
                      <SelectItem value="quarterly">3 mois</SelectItem>
                      <SelectItem value="semiannual">6 mois</SelectItem>
                      <SelectItem value="annual">1 an</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Ambassadeur */}
                <div className="flex items-center justify-between p-3 bg-dark-subtle rounded">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-gray-300">Ambassadeur</span>
                    <span className="text-xs text-gray-500">(accès validation profils)</span>
                  </div>
                  <Switch
                    checked={editUser.is_ambassador}
                    onCheckedChange={(checked) => setEditUser({ ...editUser, is_ambassador: checked })}
                  />
                </div>

                {/* Certifié - Grisé car géré par Veriff */}
                <div className="flex items-center justify-between p-3 bg-dark-subtle/50 rounded opacity-60">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gold" />
                    <span className="text-sm text-gray-400">Certifié</span>
                    <span className="text-xs text-gray-500">(via Veriff uniquement)</span>
                  </div>
                  <Switch
                    checked={editUser.is_certified}
                    disabled={true}
                    className="opacity-50"
                  />
                </div>

                {/* Password Reset */}
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <Label className="text-gray-400 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Réinitialiser le mot de passe
                  </Label>
                  <Input
                    type="password"
                    placeholder="Nouveau mot de passe (laisser vide pour ne pas changer)"
                    value={editUser.new_password || ''}
                    onChange={(e) => setEditUser({ ...editUser, new_password: e.target.value })}
                    className="bg-dark-subtle border-white/20 text-white"
                  />
                  <p className="text-xs text-gray-500">Laissez vide pour conserver le mot de passe actuel</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setEditModalOpen(false)} className="btn-outline-gold">
                Annuler
              </Button>
              <Button onClick={updateUser} className="btn-gold">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Details Modal */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl gold-gradient-text flex items-center gap-3">
                <button onClick={() => setDetailsModalOpen(false)} className="text-gray-400 hover:text-gold">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                Fiche complète de {selectedUser?.pseudo}
              </DialogTitle>
            </DialogHeader>
            
            {loadingDetails ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
              </div>
            ) : userDetails ? (
              <ScrollArea className="h-[70vh] pr-4">
                <div className="space-y-6">
                  {/* User Info Header */}
                  <div className="flex items-start gap-6 p-4 bg-dark-subtle rounded">
                    <Avatar className="w-20 h-20 border-2 border-gold/30">
                      {userDetails.profile?.profile_picture ? (
                        <AvatarImage src={getImageUrl(userDetails.profile.profile_picture)} />
                      ) : (
                        <AvatarFallback className="bg-dark text-gold text-2xl">
                          {userDetails.user.pseudo?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-serif text-white">{userDetails.user.pseudo}</h3>
                        {userDetails.user.is_certified && (
                          <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs">Certifié</span>
                        )}
                        {userDetails.user.is_subscribed && (
                          <span className="px-2 py-0.5 bg-gold text-dark text-xs font-semibold">Ambassadeur</span>
                        )}
                        {userDetails.user.is_verified_by_team && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs">Vérifié</span>
                        )}
                        {userDetails.user.is_admin && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs">Admin</span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm flex items-center gap-2">
                        <Mail className="w-4 h-4" /> {userDetails.user.email}
                      </p>
                      {userDetails.profile && (
                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {userDetails.profile.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" /> {userDetails.profile.age} ans
                          </span>
                          <span className="capitalize">{userDetails.profile.profile_type}</span>
                          <span className="capitalize">{userDetails.profile.body_type}</span>
                        </div>
                      )}
                    </div>
                    <Button 
                      onClick={() => { setDetailsModalOpen(false); openEditModal(userDetails.user); }}
                      className="btn-outline-gold"
                    >
                      <Edit2 className="w-4 h-4 mr-2" /> Modifier
                    </Button>
                  </div>

                  {/* Description */}
                  {userDetails.profile?.description && (
                    <div className="p-4 bg-dark-subtle rounded">
                      <h4 className="text-gold text-sm uppercase tracking-widest mb-2">Description</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">{userDetails.profile.description}</p>
                    </div>
                  )}

                  {/* Preferences */}
                  {userDetails.profile?.sexual_preferences?.length > 0 && (
                    <div className="p-4 bg-dark-subtle rounded">
                      <h4 className="text-gold text-sm uppercase tracking-widest mb-2">Préférences</h4>
                      <div className="flex flex-wrap gap-2">
                        {userDetails.profile.sexual_preferences.map(pref => (
                          <span key={pref} className="px-2 py-1 bg-gold/10 text-gold text-xs border border-gold/30">
                            {pref}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Photos */}
                  <div className="p-4 bg-dark-subtle rounded">
                    <h4 className="text-gold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Image className="w-4 h-4" /> Photos publiques ({userDetails.profile?.photos?.length || 0})
                    </h4>
                    {userDetails.profile?.photos?.length > 0 ? (
                      <div className="grid grid-cols-4 gap-2">
                        {userDetails.profile.photos.map((photo, index) => (
                          <div key={photo.id || index} className="relative group aspect-square">
                            <img
                              src={getImageUrl(photo.url)}
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover rounded"
                            />
                            <button
                              onClick={() => deletePhoto(userDetails.user.id, photo.id)}
                              className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3 text-white" />
                            </button>
                            <div className="absolute bottom-1 left-1 flex items-center gap-1 text-xs text-white bg-black/50 px-1 rounded">
                              <Heart className="w-3 h-3" /> {photo.likes || 0}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucune photo publique</p>
                    )}
                  </div>

                  {/* Private Photos */}
                  <div className="p-4 bg-dark-subtle rounded">
                    <h4 className="text-gold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Lock className="w-4 h-4" /> Photos privées ({userDetails.profile?.private_photos?.length || 0})
                    </h4>
                    {userDetails.profile?.private_photos?.length > 0 ? (
                      <div className="grid grid-cols-4 gap-2">
                        {userDetails.profile.private_photos.map((photo, index) => (
                          <div key={photo.id || index} className="relative group aspect-square">
                            <img
                              src={getImageUrl(photo.url)}
                              alt={`Private ${index + 1}`}
                              className="w-full h-full object-cover rounded"
                            />
                            <button
                              onClick={() => deletePhoto(userDetails.user.id, photo.id)}
                              className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3 text-white" />
                            </button>
                            <div className="absolute top-1 left-1">
                              <Lock className="w-4 h-4 text-gold" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucune photo privée</p>
                    )}
                  </div>

                  {/* Messages */}
                  <div className="p-4 bg-dark-subtle rounded">
                    <h4 className="text-gold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Messages ({userDetails.messages?.length || 0})
                    </h4>
                    {userDetails.messages?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {userDetails.messages.slice(0, 20).map((msg) => (
                          <div key={msg.id} className="p-2 bg-dark rounded text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className={msg.sender_id === userDetails.user.id ? 'text-gold' : 'text-gray-400'}>
                                {msg.sender_id === userDetails.user.id ? `→ Envoyé à ${msg.partner_pseudo || 'Inconnu'}` : `← Reçu de ${msg.partner_pseudo || msg.sender_pseudo || 'Inconnu'}`}
                              </span>
                              <span className="text-gray-500 text-xs">
                                {new Date(msg.created_at).toLocaleString('fr-FR')}
                              </span>
                            </div>
                            <p className="text-gray-300">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucun message</p>
                    )}
                  </div>

                  {/* Chat Messages */}
                  <div className="p-4 bg-dark-subtle rounded">
                    <h4 className="text-gold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Send className="w-4 h-4" /> Chat en temps réel ({userDetails.chat_messages?.length || 0})
                    </h4>
                    {userDetails.chat_messages?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {userDetails.chat_messages.slice(0, 20).map((msg, index) => (
                          <div key={msg.id || index} className="p-2 bg-dark rounded text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className={msg.sender_id === userDetails.user.id ? 'text-gold' : 'text-gray-400'}>
                                {msg.sender_id === userDetails.user.id ? `→ Envoyé à ${msg.partner_pseudo || 'Inconnu'}` : `← Reçu de ${msg.partner_pseudo || msg.sender_pseudo || 'Inconnu'}`}
                              </span>
                              <span className="text-gray-500 text-xs">
                                {new Date(msg.created_at).toLocaleString('fr-FR')}
                              </span>
                            </div>
                            <p className="text-gray-300">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucun message chat</p>
                    )}
                  </div>

                  {/* Validation Votes */}
                  <div className="p-4 bg-dark-subtle rounded">
                    <h4 className="text-gold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Check className="w-4 h-4" /> Votes de validation ({userDetails.validation_votes?.length || 0}/5)
                    </h4>
                    {userDetails.validation_votes?.length > 0 ? (
                      <div className="space-y-2">
                        {userDetails.validation_votes.map((vote, index) => (
                          <div key={vote.id || index} className="flex items-center justify-between p-2 bg-dark rounded">
                            <span className="text-gray-400 text-sm">Vote #{index + 1}</span>
                            <span className={vote.approved ? 'text-green-400' : 'text-red-400'}>
                              {vote.approved ? '✓ Approuvé' : '✗ Refusé'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucun vote reçu</p>
                    )}
                  </div>
                </div>
              </ScrollArea>
            ) : (
              <p className="text-gray-500 text-center py-12">Impossible de charger les détails</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Verification Review Modal */}
        <Dialog open={verificationModalOpen} onOpenChange={setVerificationModalOpen}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl gold-gradient-text flex items-center gap-3">
                <Camera className="w-6 h-6" />
                Vérification de {selectedVerification?.pseudo}
              </DialogTitle>
            </DialogHeader>
            
            {selectedVerification && (
              <div className="space-y-6 py-4">
                {/* User Info */}
                <div className="flex items-center gap-4 p-4 bg-dark-subtle rounded">
                  <Avatar className="w-16 h-16 border-2 border-gold/30">
                    {selectedVerification.profile?.profile_picture ? (
                      <AvatarImage src={getImageUrl(selectedVerification.profile.profile_picture)} />
                    ) : (
                      <AvatarFallback className="bg-dark text-gold text-xl">
                        {selectedVerification.pseudo?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div>
                    <h3 className="text-white font-semibold text-lg">{selectedVerification.pseudo}</h3>
                    <p className="text-gray-500 text-sm">
                      Demande soumise le {new Date(selectedVerification.submitted_at).toLocaleDateString('fr-FR')} 
                      à {new Date(selectedVerification.submitted_at).toLocaleTimeString('fr-FR')}
                    </p>
                  </div>
                </div>

                {/* Instructions */}
                <div className="p-4 bg-gold/10 border border-gold/30 rounded">
                  <p className="text-gold text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Vérifiez que les photos montrent clairement le visage du membre avec la date du jour 
                    et le nom "DiscreetLux" écrit sur un papier visible.
                  </p>
                </div>

                {/* Verification Photos */}
                <div>
                  <h4 className="text-gold text-sm uppercase tracking-widest mb-3">Photos de vérification</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedVerification.photos?.map((photo, index) => (
                      <div key={index} className="aspect-square overflow-hidden rounded bg-dark-subtle">
                        <img
                          src={getImageUrl(photo)}
                          alt={`Vérification ${index + 1}`}
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => window.open(getImageUrl(photo), '_blank')}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-500 text-xs mt-2">Cliquez sur une photo pour l'agrandir</p>
                </div>

                {/* Rejection Reason */}
                <div>
                  <Label className="text-gray-400 mb-2 block">Motif de refus (si applicable)</Label>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Ex: Photos floues, date non visible, nom du site absent..."
                    className="bg-dark-subtle border-white/20 text-white"
                    rows={3}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button 
                    onClick={() => respondToVerification(false)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    disabled={!rejectionReason.trim()}
                  >
                    <X className="w-4 h-4 mr-2" /> Refuser
                  </Button>
                  <Button 
                    onClick={() => respondToVerification(true)}
                    className="flex-1 btn-gold"
                  >
                    <Check className="w-4 h-4 mr-2" /> Approuver
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Report Detail Modal */}
        <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl gold-gradient-text flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                Détail du signalement
              </DialogTitle>
            </DialogHeader>
            
            {selectedReport && (
              <div className="space-y-6 py-4">
                {/* Reported User Info */}
                <div className="glass-card p-4">
                  <h3 className="text-gold text-sm uppercase tracking-wider mb-3">Profil signalé</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-dark-subtle">
                      {selectedReport.reported_profile?.profile_picture ? (
                        <img
                          src={getImageUrl(selectedReport.reported_profile.profile_picture)}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-500">
                          <Users className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-white text-lg">{selectedReport.reported_user_pseudo}</p>
                      <p className="text-gray-500 text-sm">{selectedReport.reported_user?.email}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-auto border-white/20"
                      onClick={() => {
                        setReportModalOpen(false);
                        // Open user details
                        const u = users.find(u => u.id === selectedReport.reported_user_id);
                        if (u) {
                          setSelectedUser(u);
                          fetchUserDetails(u.id);
                          setDetailsModalOpen(true);
                        }
                      }}
                    >
                      <Eye className="w-4 h-4 mr-2" /> Voir profil complet
                    </Button>
                  </div>
                </div>

                {/* Report Details */}
                <div className="glass-card p-4">
                  <h3 className="text-gold text-sm uppercase tracking-wider mb-3">Raison du signalement</h3>
                  <div className="flex items-center gap-2 text-red-400 mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">{selectedReport.reason_label}</span>
                  </div>
                  {selectedReport.details && (
                    <div className="mt-3 p-3 bg-dark-subtle rounded-lg">
                      <p className="text-gray-300 text-sm italic">"{selectedReport.details}"</p>
                    </div>
                  )}
                </div>

                {/* Reporter Info */}
                <div className="glass-card p-4">
                  <h3 className="text-gold text-sm uppercase tracking-wider mb-3">Signalé par</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white">{selectedReport.reporter_pseudo}</p>
                      <p className="text-gray-500 text-sm">
                        {new Date(selectedReport.created_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Admin Notes */}
                <div>
                  <Label className="text-gray-400 mb-2 block">Notes admin</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Ajouter des notes sur ce signalement..."
                    className="bg-dark-subtle border-white/20 text-white"
                    rows={3}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => updateReportStatus(selectedReport.id, 'reviewed')}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Eye className="w-4 h-4 mr-2" /> Marquer comme examiné
                  </Button>
                  <Button
                    onClick={() => updateReportStatus(selectedReport.id, 'action_taken')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Check className="w-4 h-4 mr-2" /> Action prise
                  </Button>
                  <Button
                    onClick={() => updateReportStatus(selectedReport.id, 'dismissed')}
                    variant="outline"
                    className="flex-1 border-gray-500 text-gray-400 hover:bg-gray-500/10"
                  >
                    <X className="w-4 h-4 mr-2" /> Rejeter
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Validation Rejection Modal */}
        <Dialog open={validationRejectionModalOpen} onOpenChange={setValidationRejectionModalOpen}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl gold-gradient-text">
                Rejeter le profil
              </DialogTitle>
            </DialogHeader>
            {selectedValidationProfile && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4 p-4 bg-dark-subtle rounded">
                  <Avatar className="w-12 h-12 border-2 border-gold/30">
                    {selectedValidationProfile.profile?.profile_picture ? (
                      <AvatarImage src={getImageUrl(selectedValidationProfile.profile.profile_picture)} />
                    ) : (
                      <AvatarFallback className="bg-dark-subtle text-gold">
                        {selectedValidationProfile.profile?.pseudo?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div>
                    <p className="text-white font-semibold">{selectedValidationProfile.profile?.pseudo}</p>
                    <p className="text-gray-500 text-sm">{selectedValidationProfile.user?.email}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Label className="text-gray-400">Raison du rejet</Label>
                  <Select
                    value={validationRejectionReason}
                    onValueChange={setValidationRejectionReason}
                  >
                    <SelectTrigger className="bg-dark-subtle border-white/20 text-white">
                      <SelectValue placeholder="Sélectionner une raison" />
                    </SelectTrigger>
                    <SelectContent className="bg-dark-paper border-white/10">
                      <SelectItem value="photos_not_good">Photos de mauvaise qualité</SelectItem>
                      <SelectItem value="description_insufficient">Description insuffisante</SelectItem>
                      <SelectItem value="both">Photos et description à améliorer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <p className="text-gray-400 text-sm">
                  L'utilisateur sera notifié par email et pourra modifier son profil pour une nouvelle validation.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setValidationRejectionModalOpen(false)} className="btn-outline-gold">
                Annuler
              </Button>
              <Button 
                onClick={() => handleAdminValidation(selectedValidationProfile?.user?.id, false)}
                disabled={!validationRejectionReason}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Rejeter le profil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value, highlight = false }) => (
  <div className={`glass-card p-6 ${highlight ? 'border border-gold/50' : ''}`}>
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-full ${highlight ? 'bg-gold/30' : 'bg-gold/10'} flex items-center justify-center text-gold`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold gold-gradient-text">{value}</p>
        <p className="text-gray-500 text-sm">{label}</p>
      </div>
    </div>
  </div>
);
